/**
 * Pure helpers for the EIN section's "Type of Entity" answer.
 *
 * The order payload nests every EIN answer under `form.ein`, and the backend
 * validates the nested key — `ein.ein_entity_type_corporation_specified`, see
 * App\Rules\ForceCorporationTypeOnCorporationEntity, which reads
 * request('ein.ein_entity_type_corporation_specified').
 *
 * Writing the answer to the top level of the form instead looks correct on
 * screen (the DropDown renders its own internal selection, so it still shows
 * "C-Corp") while shipping `ein.ein_entity_type_corporation_specified: null`.
 * Every such order came back 422 with "You must specify the type of tax form
 * you will file." and no field highlighted, because no input is bound to the
 * top-level key the value landed on.
 */

/** Key inside `form.ein`. */
export const CORPORATION_TYPE_FIELD = 'ein_entity_type_corporation_specified';

/** Key the backend returns validation errors under, and `form.errors` stores. */
export const CORPORATION_TYPE_ERROR_KEY = `ein.${CORPORATION_TYPE_FIELD}`;

/**
 * Record the chosen corporation type (C-Corp / S-Corp) on the order form.
 *
 * Mutates the form in place — it is the same reactive proxy the parent owns —
 * and returns it so callers can chain or assert. Clearing the field's stale
 * validation error on a real selection mirrors OrderSection's
 * removeErrorsOnUpdate(): an error survives only while the field is empty.
 *
 * @param  {object|null} form   The order form (expects a nested `ein` object).
 * @param  {string|null} value  'C-Corp', 'S-Corp', or null/'' to clear.
 * @return {object|null}        The same form.
 */
export function applyCorporationType(form, value) {
    if (!form || typeof form !== 'object') {
        return form;
    }

    if (!form.ein || typeof form.ein !== 'object') {
        form.ein = {};
    }

    form.ein[CORPORATION_TYPE_FIELD] = value === undefined || value === '' ? null : value;

    if (form.ein[CORPORATION_TYPE_FIELD] && form.errors && typeof form.errors === 'object') {
        form.errors[CORPORATION_TYPE_ERROR_KEY] = null;
    }

    return form;
}

/**
 * Validation messages to render beside the corporation dropdown.
 *
 * Laravel returns an array of messages per key, but OrderSection seeds
 * `form.errors` with nulls and other call sites assign bare strings, so
 * normalise to an array the template can always v-for over.
 *
 * @param  {object|null} form
 * @return {string[]}
 */
export function corporationTypeErrors(form) {
    const raw = form && form.errors ? form.errors[CORPORATION_TYPE_ERROR_KEY] : null;

    if (!raw) {
        return [];
    }

    return (Array.isArray(raw) ? raw : [raw]).filter(Boolean);
}

/**
 * Entity-type radio value for a document's company classification.
 *
 * CompanyType::getGeneralClassificationAttribute() returns LLC / CORP /
 * NON-PROFIT / TRUST. Two of those had no home in the EIN form: 'NON-PROFIT'
 * was assigned verbatim even though no radio carries that value — so nothing
 * appeared selected, `required|string` passed anyway, and the SS-4's entity
 * switch fell through to `default: break;`, printing no entity type at all.
 * 'TRUST' fell into the else and was silently pre-set to LLC despite a Trust
 * radio existing.
 *
 * Every value returned here matches a radio in FormEinSection and a case in
 * PDFCreateEINApplicationService. Unknown or missing classifications keep the
 * previous LLC default — it is the commonest filing and stays user-editable.
 *
 * @param  {string|null} classification
 * @return {string}
 */
export function entityTypeForClassification(classification) {
    switch (classification) {
        case 'LLC':
            return 'LLC';
        case 'CORP':
            return 'corporation';
        case 'NON-PROFIT':
            return 'other_non_profit';
        case 'TRUST':
            return 'trust';
        default:
            return 'LLC';
    }
}
